import 'twin.macro';
import React from 'react';
import Button from '@teetsh/app/src/components/Buttons/Button';
import {
  Planner,
  PlannerCalendarPDFSettings,
} from '@teetsh/app/src/types/planner';
import useDownloadPlanner from '@teetsh/app/src/queries/planners/useDownloadPlanner';
import {
  QueueListIcon,
  PrinterIcon,
  ArrowDownTrayIcon,
  PaintBrushIcon,
} from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { UserLocale } from '@teetsh/app/src/types/user';
import { Field, useFormState } from 'react-final-form';
import { SidebarWithSections } from '@teetsh/app/src/components/SubSidebar/SidebarWithSections';
import FormGroup from '@teetsh/app/src/components/Form/FormGroup';
import InputGroup from '@teetsh/app/src/components/Form/InputGroup';
import { FontsCoupleSelector } from '@teetsh/app/src/components/Form/FontsCoupleSelector';
import { FontSizeSelector } from '@teetsh/app/src/components/FontSizeSelector';
import { ColorSelector } from '@teetsh/app/src/components/Form/ColorSelector';
import { BgImageSelectorWithImages } from '@teetsh/app/src/components/Form/BgImageSelector/BgImageSelectorWithImages';
import { MarginsFieldWrapper } from '@teetsh/app/src/components/Margins';
import { Checkboxes } from 'components/Form/Checkboxes';
import { Checkbox } from 'components/Form/Checkboxes/Checkbox';
import { ReunionCategory } from 'types/reunions';

interface Props {
  planner: Planner;
  onUpdatePlanner: (v: Planner) => void;
  date: Date;
  lng: UserLocale;
  limitedBgSet: boolean;
  categories: ReunionCategory[];
  selectedCategories: string[];
  setSelectedCategories: (categories: string[]) => void;
}

export function Sidebar(props: Props) {
  const {
    planner,
    onUpdatePlanner,
    date,
    lng,
    limitedBgSet,
    categories = [],
    selectedCategories,
    setSelectedCategories,
  } = props;
  const { values } = useFormState<PlannerCalendarPDFSettings>();
  const { download, isDownloading } = useDownloadPlanner();
  const onDownload = () =>
    download({
      planner,
      date,
      lng,
      settings: values,
      selectedCategories,
    });
  const { t } = useTranslation(['common', 'planner']);

  return (
    <SidebarWithSections
      collapsedComponent={
        <SidebarWithSections.CollapsedButton
          icon={ArrowDownTrayIcon}
          title={t('common:buttons.downloadPdf')}
          loading={isDownloading}
          onClick={() => onDownload()}
        />
      }
    >
      <div tw="p-4">
        <Button
          fill
          large
          intent="primary"
          icon={ArrowDownTrayIcon}
          loading={isDownloading}
          onClick={() => onDownload()}
        >
          {t('common:buttons.downloadPdf')}
        </Button>
      </div>

      <SidebarWithSections.Section
        icon={QueueListIcon}
        title={t('common:pdf.elementsAndDisposition')}
      >
        <FormGroup label={t('planner:calendar.print.titlePlaceholder')}>
          <InputGroup
            placeholder={t('planner:calendar.print.titlePlaceholder')}
            value={planner.title}
            onChange={v => onUpdatePlanner({ ...planner, title: v })}
          />
        </FormGroup>
        {categories.length > 1 && (
          <FormGroup label={t('planner:selectedCategories')}>
            <Checkboxes
              condensed
              value={selectedCategories}
              onChange={setSelectedCategories}
              options={categories.map(cat => ({
                label: cat.name,
                value: cat.id,
              }))}
            />
          </FormGroup>
        )}
      </SidebarWithSections.Section>

      <SidebarWithSections.Section
        icon={PaintBrushIcon}
        title={t('common:pdf.appearance')}
      >
        <FormGroup label={t('common:pdf.fonts')}>
          <Field
            name="font"
            component={({ input: { onChange, value } }) => (
              <FontsCoupleSelector onChange={onChange} value={value} />
            )}
          />
        </FormGroup>
        <FormGroup label={t('common:pdf.fontSize')}>
          <Field
            name="fontSize"
            label={t('common:pdf.fontSize')}
            render={({ input: { onChange, value } }) => (
              <FontSizeSelector onChange={onChange} value={value} max={18} />
            )}
          />
        </FormGroup>
        <FormGroup label={t('common:pdf.bgColor')}>
          <Field
            name="bgColor"
            render={({ input: { value, onChange } }) => (
              <ColorSelector light value={value} onChange={onChange} />
            )}
          />
        </FormGroup>
        <FormGroup label={t('common:pdf.bgImages')}>
          <Field
            name="bgColor"
            render={({ input: { value, onChange } }) => (
              <BgImageSelectorWithImages
                value={value}
                onChange={onChange}
                limitedBgSet={limitedBgSet}
              />
            )}
          />
        </FormGroup>
      </SidebarWithSections.Section>

      <SidebarWithSections.Section
        icon={PrinterIcon}
        title={t('common:pdf.layout')}
      >
        <FormGroup label={t('common:pdf.margins')}>
          <MarginsFieldWrapper />
        </FormGroup>
        <Field
          name="withPagination"
          type="checkbox"
          render={({ input }) => (
            <Checkbox
              id="withPagination"
              label={t('common:pdf.withPagination')}
              description={t('common:pdf.onlyVisibleOnPdf')}
              {...input}
            />
          )}
        />
      </SidebarWithSections.Section>
    </SidebarWithSections>
  );
}
